import React from "react";
import { Form, Button, Alert, Modal, Dropdown, OverlayTrigger, Popover } from "react-bootstrap";
import { VscSend } from "react-icons/vsc";
import { LuPlus, LuX, LuPaperclip, LuImage } from "react-icons/lu";
import { CgDetailsMore } from "react-icons/cg";
import Editor from "components/controls/editor";
import Tooltip from "../../../controls/common/tooltip";
import ThreadView from "../common/threadView";
import WidgetBase from "../common/widgetBase";
import Alias from "components/controls/alias";
import EnvelopeControls from "../common/envelopeControls";
import { Converter } from "../../workspace/converter";
import { canMutateSession } from "../../session/mutationGate";
import {
  appendStaged, filesFromDataTransfer, filesFromClipboard, buildAlias, insertToken, stripToken, tokenSpans,
  interleaveContent
} from "./attachments";
import { seedFileUrl } from "../common/fileBlocks";
import Navigation from "context/nav";
import Utils from "context/utils";
import { withPreferences } from "context/preferences";
import "./index.css";

// Custom Dropdown toggle for the composer's "+" button. Lets us keep the
// existing circular styling while delegating click/keyboard handling to
// react-bootstrap's Dropdown.
const ComposerAttachToggle = React.forwardRef(({ onClick }, ref) => (
  <button
    ref={ref}
    type="button"
    className="playground-thread-composer-attach"
    onClick={(e) => { e.preventDefault(); onClick(e); }}
    aria-label="Attachment menu"
  >
    <LuPlus />
  </button>
));

const PAGE_LIMIT = 10;
const THREAD_DRAIN_MAX_PAGES = 1000;

// A staged chip's contribution to a post's content array, once it carries an alias — either freshly
// uploaded, or already uploaded on a prior send attempt that this one is retrying.
function fileBlockFor(chip) {
  return {
    type: "file",
    file: { alias: chip.alias, name: chip.name, content_type: chip.contentType, size: chip.size }
  };
}

// Exported (named) for unit tests that inspect the raw class directly; the
// default export below is the withPreferences-wrapped component used by the app.
export class ThreadNode extends WidgetBase {
  constructor(props) {
    super(props);

    this.state = {
      posting: false,
      inputValue: "",
      currentUserId: null,
      postError: null,
      // Composer metadata attached to the next send.
      composerMetadata: null,            // parsed object or null
      composerMetadataModalVisible: false,
      composerMetadataDraft: "",         // textarea contents in the modal
      composerMetadataError: null,
      // Files staged for the next send — chips built by appendStaged() (attachments.js). Each is
      // `{ id, file, name, contentType, size, status, error, alias }`; status "staged" (sendable) or
      // "rejected" (a client-side validation failure that blocks send until removed).
      pendingFiles: [],
    };

    // The read-only view: mounted below, and the handle through which a post shows itself before
    // the server has it.
    this.view = React.createRef();
    this.nodeRef = React.createRef();
    this.textareaRef = React.createRef();
    this.fileInputRef = React.createRef();
    this.pendingTempIds = new Set();
    this.authorPromises = {};
    this.boundAlias = null;
    this.boundCallback = null;
    this.boundSessionId = null;
    this.mounted = false;
    // Object URLs for the staged-image hover previews, one per chip id. Created lazily from the local
    // File (no upload) and revoked when the chip leaves pendingFiles (removed or cleared after send).
    this.previewUrls = new Map();
  }

  /**
   * Lifecycle
   */

  componentDidMount() {
    super.componentDidMount();
    this.mounted = true;

    const api = this.getApi();
    if ( api && api.getCurrentUserId ) {
      api.getCurrentUserId().then( (id) => {
        if ( !this.mounted ) return;
        this.setState({ currentUserId: id });
      }, (_error) => {
        // Anonymous — leave currentUserId as null
      });
    }

  }

  componentDidUpdate(prevProps) {
    super.componentDidUpdate(prevProps);
    this.checkSyncResume();
    this.cleanupPreviewUrls();
  }

  // Revoke the object URL of any chip no longer staged — this catches both removeStagedFile and the
  // clear-after-send in one place, so no removal path leaks. Called after every update.
  cleanupPreviewUrls() {
    if ( !this.previewUrls || 0 === this.previewUrls.size ) return;
    const live = new Set((this.state.pendingFiles || []).map((chip) => chip.id));
    this.previewUrls.forEach((url, id) => {
      if ( !live.has(id) ) {
        try { URL.revokeObjectURL(url); } catch (e) { /* ignore */ }
        this.previewUrls.delete(id);
      }
    });
  }

  // The staged image's own File is previewable locally — no upload, no fetch, and no new security
  // surface (it is the user's own file in their own tab). Cached per chip so it is created once, and
  // guarded so a runtime without createObjectURL (jsdom) simply gets no thumbnail.
  previewUrlFor(chip) {
    if ( !chip.file || typeof URL === "undefined" || typeof URL.createObjectURL !== "function" ) return null;
    if ( !this.previewUrls.has(chip.id) ) {
      try { this.previewUrls.set(chip.id, URL.createObjectURL(chip.file)); }
      catch (e) { return null; }
    }
    return this.previewUrls.get(chip.id);
  }
  /**
   * When sync flips OFF -> ON, actively re-tail rather than trusting the event channel.
   *
   * The catch-up dispatches `session_thread_item_posted` events, but the widget cannot rely on them
   * alone: the baseline it would fetch from is the user's OWN latest post, so items written by other
   * actors before it would be missed, and the channel itself has edges — a toggle racing an
   * outstanding POST, polling pausing after 180s. Asking the view to resync covers both: it fetches
   * strictly past what it holds, or loads the thread when it holds nothing to fetch past.
   */
  checkSyncResume() {
    const currentSync = !!(this.context && this.context.sessionSync);
    const prevSync = this._lastSyncState;
    this._lastSyncState = currentSync;

    if ( prevSync !== false || currentSync !== true ) return;
    if ( this.getEnvelopeState().status !== "ready" ) return;
    if ( this.view.current ) this.view.current.resync();
  }

  /**
   * What identifies the thread this widget is showing — the view reloads when it changes.
   *
   * The same three facts the reload gate always compared: which alias, in which session, and whether
   * the envelope is ready to be read at all. Firing a fetch against an envelope that is still
   * loading, missing or of the wrong type produces an `invalid_thread_envelope` error and a second,
   * confusing warning in the body — so "not ready" is its own key, and the view holds nothing.
   */
  sourceKey() {
    const ready = this.getEnvelopeState().status === "ready";
    return `${this.getBoundAlias() || ""}|${this.getSessionId() || ""}|${ready ? "ready" : "wait"}`;
  }

  /**
   * Where the view's bytes come from. Reads only — posting stays here, on top of it.
   */
  threadSource() {
    // READINESS FIRST, and the ids only after it. This runs during render now, where the old reload
    // gate ran only once the envelope was settled — so reading a revision off a shell that has not
    // got one yet is no longer a thing that cannot happen.
    const ready = this.getEnvelopeState().status === "ready";
    const alias = this.getBoundAlias();
    const sessionId = this.getSessionId();
    if ( !ready || !alias || !sessionId ) {
      // Nothing to read: the body is showing a banner about why, and a fetch here would add a
      // second one contradicting it.
      return { key: this.sourceKey() };
    }
    const revisionId = this.getRevisionId();
    const api = this.getApi();
    if ( !revisionId ) return { key: this.sourceKey() };
    return {
      key: this.sourceKey(),
      listOlder: ({ cursor }) => {
        const options = { ascending: false, limit: PAGE_LIMIT };
        if ( cursor ) options.cursor = cursor;
        return api.threadItemsList(revisionId, sessionId, alias, options).then((response) => {
          const data = (response && response.data) || {};
          // Newest-first on the wire, oldest-first on screen.
          return { items: (data["items"] || []).slice().reverse(), cursor: data.cursor || null };
        });
      },
      listNewer: ({ since }) => this.drainNewer(revisionId, sessionId, alias, since),
      watch: (callback) => {
        this.boundCallback = callback;
        this.boundAlias = alias;
        if ( this.context ) this.context.addThreadEventListener(alias, callback);
      },
      unwatch: (callback) => {
        if ( this.boundAlias && this.context ) {
          this.context.removeThreadEventListener(this.boundAlias, callback);
        }
        this.boundAlias = null;
        this.boundCallback = null;
      },
      authorName: (userId) => this.resolveAuthorName(userId),
      authorProfilePath: (userId) => this.resolveProfilePath(userId)
    };
  }

  /**
   * Where a `file` content block's bytes come from — the same {api, revisionId, sessionId} that
   * threadSource() reads the thread itself with, and gated the same way: a shellInterface that has
   * not yet got a revision or a session (getApi()/getRevisionId() throw on one that hasn't) must
   * not be asked for one just because a file block happens to be on screen.
   */
  fileContext() {
    if ( this.getEnvelopeState().status !== "ready" ) return undefined;
    const alias = this.getBoundAlias();
    const sessionId = this.getSessionId();
    if ( !alias || !sessionId ) return undefined;
    const revisionId = this.getRevisionId();
    if ( !revisionId ) return undefined;
    return { api: this.getApi(), revisionId, sessionId };
  }

  /**
   * Everything strictly newer than `since`, following the cursor across pages.
   *
   * Paged because a tail can be a burst larger than one page — a sync-resume backlog is the case
   * that made it necessary. Bounded so a cursor that never clears cannot spin forever.
   */
  async drainNewer(revisionId, sessionId, alias, since) {
    const api = this.getApi();
    const collected = [];
    const seen = new Set();
    let cursor = null;
    for ( let page = 0; page < THREAD_DRAIN_MAX_PAGES; page++ ) {
      const options = { ascending: true, createdSince: since, limit: PAGE_LIMIT };
      if ( cursor ) options.cursor = cursor;
      const response = await api.threadItemsList(revisionId, sessionId, alias, options);
      if ( !this.mounted ) return [];
      const raw = (response.data && response.data["items"]) || [];
      for ( const item of raw ) {
        if ( item && !seen.has(item.id) ) {
          seen.add(item.id);
          collected.push(item);
        }
      }
      cursor = (response.data && response.data.cursor) || null;
      if ( !cursor ) break;
    }
    return collected;
  }

  componentWillUnmount() {
    this.mounted = false;
    this.previewUrls.forEach((url) => { try { URL.revokeObjectURL(url); } catch (e) { /* ignore */ } });
    this.previewUrls.clear();
    super.componentWillUnmount();
  }
  /**
   * Accessors
   */

  getApi() {
    return this.props.data.shellInterface.getApp().api;
  }

  getRevisionId() {
    return this.props.data.shellInterface.getRevision()["id"];
  }

  getSessionId() {
    return this.context && this.context.session ? this.context.session.id : null;
  }

  getBoundAlias() {
    return Converter.getComponentPortValueData(this.props.data.component, "thread");
  }

  // Inspect session-manager state to decide what we can render and whether
  // it's safe to call the backend. Returns:
  //   - { status: "unbound" }        — no thread port binding
  //   - { status: "no-session" }     — alias set, no session selected
  //   - { status: "loading" }        — session-objects list or value still loading
  //   - { status: "missing" }        — list loaded, no object with this alias
  //   - { status: "wrong-type" }     — object exists but value.type !== "thread"
  //   - { status: "ready", value }   — object exists and is a thread
  // This also registers a subscription on the alias via `discoveredAliases`
  // so the widget re-renders when the value becomes available.
  getEnvelopeState() {
    const alias = this.getBoundAlias();
    if ( !alias ) return { status: "unbound" };

    const manager = this.context;
    if ( !manager || !manager.session ) return { status: "no-session" };

    // Subscribe to the alias so this widget re-renders when its value
    // arrives. Cleared per-render by WidgetBase.updateSubscriptions().
    this.discoveredAliases.add(alias);

    const sessionObjects = manager.sessionObjects;
    if ( sessionObjects === null || sessionObjects === undefined ) {
      return { status: "loading", reason: "Loading session objects..." };
    }

    const exists = sessionObjects.some(o => o.alias === alias);
    if ( !exists ) {
      return { status: "missing", alias };
    }

    const value = manager.getSessionData ? manager.getSessionData(alias) : undefined;
    if ( value === undefined || value === null ) {
      return { status: "loading", reason: "Loading thread..." };
    }

    if ( value.type !== "thread" ) {
      return { status: "wrong-type", alias, type: value.type || "unknown" };
    }

    return { status: "ready", value };
  }
  /**
   * Posting
   */

  // Drives the Send button's `disabled` prop only — NOT a security boundary on its own. sendItem()
  // below re-checks canMutateSession() as the actual choke point, since the Enter-key handler, the
  // composer's <Form onSubmit>, and the failed-item retry button all bypass this method entirely.
  canSend() {
    const si = this.props.data.shellInterface;
    const pendingFiles = this.state.pendingFiles || [];
    // A staged, non-rejected file counts as content on its own (an attachment-only send is valid),
    // but ANY rejected chip — a client-side validation failure — blocks send until it is removed.
    const hasSendableFile = pendingFiles.some((chip) => "rejected" !== chip.status);
    const hasRejectedFile = pendingFiles.some((chip) => "rejected" === chip.status);
    return !!(
      (this.state.inputValue.trim() || hasSendableFile)
      && !hasRejectedFile
      && !this.state.posting
      && this.state.currentUserId
      && this.getEnvelopeState().status === "ready"
      // Posting a thread item is a session-data mutation, so — beyond write access — the session must
      // also be open (Task 5). See session/mutationGate.js for the shared fail-open default applied
      // when a shellInterface doesn't expose the full seam.
      && canMutateSession(si)
    );
  }

  // Tooltip copy for the composer Send button. The composer only renders while mutations are possible
  // (see showComposer in renderContent), so there is no blocked state to explain here — a disabled
  // Send means the draft is empty or a post is already in flight.
  sendTooltipText() {
    return "Send (Shift+Enter for newline)";
  }

  sendItem(textOverride) {
    // Posting a thread item is a session-data mutation, so this is the single choke point that must
    // check the session-mutation gate — the Enter-key handler, the composer's <Form onSubmit>, and the
    // failed-item retry button all funnel through here. canSend()'s check only drives the Send button's
    // `disabled` prop; it does not, on its own, stop those other paths. Mirrors the guard placement in
    // ChatNode.sendMessage() and GraphNode.applyMutations(). The same reasoning applies to the
    // rejected-chip check just below: canSend() also uses it only to disable the button, so it is
    // re-checked here too.
    if ( !canMutateSession(this.props.data.shellInterface) ) return;

    const text = (textOverride !== undefined ? textOverride : this.state.inputValue).trim();
    const pendingFiles = this.state.pendingFiles || [];
    // A client-side validation failure ("rejected") blocks send until the chip is removed — same rule
    // canSend() enforces for the button. An attachment-only send (empty text, at least one staged
    // file) is otherwise valid; empty text with no staged files is not. Only one send runs at a time.
    const hasRejectedFile = pendingFiles.some((chip) => "rejected" === chip.status);
    if ( hasRejectedFile || (!text && 0 === pendingFiles.length) || this.state.posting ) return;

    const alias = this.getBoundAlias();
    const sessionId = this.getSessionId();
    const revisionId = this.getRevisionId();
    if ( !alias || !sessionId || !revisionId ) return;

    const metadata = this.state.composerMetadata; // null if no attachment

    // `posting` flips true here, before the upload pass even starts, so a second Send click during
    // upload is a no-op rather than a second concurrent attempt.
    this.setState({ posting: true, postError: null });

    this.uploadStagedFiles(revisionId, sessionId, alias).then(({ ok, blocks }) => {
      if ( !ok ) {
        // The failing chip already carries its own status/error (set inside uploadStagedFiles);
        // nothing was posted, so there is nothing to unwind. A later Send re-uploads only what's left.
        this.setState({ posting: false });
        return;
      }

      // Interleave the uploaded file blocks into the text at their [name] token positions (option B),
      // rather than appending all files after all text. A token whose file is unattached stays literal
      // text; an attached file with no token appends at the end. See attachments.js#interleaveContent.
      const content = interleaveContent(text, blocks);
      if ( content.length === 0 ) {
        this.setState({ posting: false });
        return;
      }

      this.setState({
        inputValue: "",
        // Clear the attached metadata and staged files once they have been queued — a fresh
        // attachment is required for the next send.
        composerMetadata: null,
        pendingFiles: []
      }, () => {
        // Reset textarea height after clearing input, and keep focus in the composer so the user can
        // start typing the next message immediately. (The button's onClick path moves focus to the
        // button; this restores it.)
        const ta = this.textareaRef.current;
        if ( ta ) {
          ta.style.height = "auto";
          ta.focus();
        }
      });

      this.postContent(content, metadata || undefined);
    }, (error) => {
      // Three statements in uploadStagedFiles run outside its inner try (getApi, buildAlias — whose
      // Utils.generateUniqueId() throws where crypto.randomUUID is undefined, e.g. an insecure
      // context — and seedFileUrl), so this promise CAN reject. With no rejection handler here that
      // used to leave `posting` stuck true forever with no error shown — the composer wedged, only a
      // reload recovered it. A rejection is exactly as much a "this send failed" as an upload or post
      // failure, so it gets the same posting:false/postError treatment those already do.
      if ( this.mounted ) {
        this.setState({ posting: false, postError: (error && error.message) || "Failed to send." });
      }
    });
  }

  // The optimistic post half — tempId, appendLocal, threadItemPost, settleLocal/failLocal — shared by
  // sendItem() (freshly built content) and retryFailedItem() (a failed item's original content and
  // metadata, verbatim). Resolves its own alias/session/revision so either caller can invoke it with
  // just the payload; owns `posting` from here onward, exactly as the pre-upload tail always did.
  postContent(content, metadata) {
    const alias = this.getBoundAlias();
    const sessionId = this.getSessionId();
    const revisionId = this.getRevisionId();
    if ( !alias || !sessionId || !revisionId ) return;

    const tempId = Utils.generateUniqueId();
    this.pendingTempIds.add(tempId);

    const optimisticItem = {
      id: tempId,
      __tempId: tempId,
      __pending: true,
      user_id: this.state.currentUserId,
      content,
      created_at: Utils.currentTimeUtcIso()
    };
    if ( metadata ) {
      optimisticItem.metadata = metadata;
    }

    if ( this.view.current ) this.view.current.appendLocal(optimisticItem);
    this.setState({ posting: true, postError: null });

    const api = this.getApi();
    // Only forward metadata if the user actually attached some — the
    // backend's schema drops the field when undefined.
    const parentId = undefined;
    api.threadItemPost(revisionId, sessionId, alias, content, parentId, metadata || undefined)
      .then( (response) => {
        if ( !this.mounted ) return;
        const serverItem = response.data && response.data["item"];
        if ( !serverItem ) {
          // No item in the response — the row stays, marked failed, so it can be retried.
          if ( this.view.current ) this.view.current.failLocal(tempId);
          this.setState({
            posting: false,
            postError: "Server did not return a thread item."
          });
          this.pendingTempIds.delete(tempId);
          return;
        }

        // The view settles it: replaced in place normally, and — because a post races the tail it
        // will arrive on — left alone if a reload already brought the server item in, or appended if
        // that reload wiped the optimistic row without picking the item up.
        if ( this.view.current ) this.view.current.settleLocal(tempId, serverItem);
        this.setState({ posting: false, postError: null });
        this.pendingTempIds.delete(tempId);
      }, (error) => {
        if ( !this.mounted ) return;
        if ( this.view.current ) this.view.current.failLocal(tempId);
        this.setState({
          posting: false,
          postError: error && error.message ? error.message : "Failed to post thread item."
        });
        // Keep tempId in pendingTempIds so the user can retry.
      });
  }

  // Sequentially uploads staged files that don't already carry an alias — a chip uploaded on a prior
  // send attempt is skipped and simply contributes its block again, in staging order. On the first
  // upload failure, that chip is marked "failed" with the server's message and the pass stops right
  // there: nothing already uploaded is undone, so the next send only re-uploads what's left. A
  // "rejected" chip (a client-side validation failure) is never uploaded and never contributes a
  // block — sendItem()'s own guard is what actually keeps one from reaching here, this is just
  // defense in depth against a caller that skips that guard.
  async uploadStagedFiles(revisionId, sessionId, threadAlias) {
    const chips = this.state.pendingFiles || [];
    const api = this.getApi();
    const blocks = [];

    for ( const chip of chips ) {
      if ( "rejected" === chip.status ) continue;

      // The chip may have been removed (removeStagedFile) after `chips` was snapshotted above, since
      // that snapshot is taken once and this loop can span the whole sequential upload pass. A chip
      // no longer in state must neither be uploaded nor contribute a block to the post — the user
      // asked for it to be unsent.
      if ( !(this.state.pendingFiles || []).some((c) => c.id === chip.id) ) continue;

      if ( chip.alias ) {
        blocks.push(fileBlockFor(chip));
        continue;
      }

      const alias = buildAlias(threadAlias, chip.name);
      let bytes;
      try {
        bytes = await this.readFileBytes(chip.file);
        await api.sessionFileUpload(revisionId, sessionId, alias, chip.name, chip.contentType, bytes);
      } catch (error) {
        const message = (error && error.message) || "Upload failed.";
        // Matches postContent()'s own response handlers: skip the setState, not the chip's outcome —
        // the upload itself already happened (or failed) regardless of whether the widget is still
        // mounted to show it.
        if ( this.mounted ) {
          this.setState((prevState) => ({
            pendingFiles: prevState.pendingFiles.map((c) => (
              c.id === chip.id ? { ...c, status: "failed", error: message } : c
            ))
          }));
        }
        return { ok: false };
      }

      seedFileUrl(revisionId, sessionId, alias, chip.file);
      if ( this.mounted ) {
        this.setState((prevState) => ({
          pendingFiles: prevState.pendingFiles.map((c) => (
            c.id === chip.id ? { ...c, status: "uploaded", alias } : c
          ))
        }));
      }
      blocks.push(fileBlockFor({ ...chip, alias }));
    }

    return { ok: true, blocks };
  }

  // FileReader, not File.prototype.arrayBuffer() — same reason as graphs/dnn/shell/index.js's own
  // upload handler: the latter isn't available in every runtime this bundle ships to (nor in jsdom).
  readFileBytes(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(reader.error || new Error("Failed to read file."));
      reader.readAsArrayBuffer(file);
    });
  }

  /**
   * Composer metadata attachment
   */

  openComposerMetadataModal = () => {
    const draft = this.state.composerMetadata
      ? JSON.stringify(this.state.composerMetadata, null, 2)
      : "{\n  \n}";
    this.setState({
      composerMetadataModalVisible: true,
      composerMetadataDraft: draft,
      composerMetadataError: null
    });
  }

  closeComposerMetadataModal = () => {
    this.setState({
      composerMetadataModalVisible: false,
      composerMetadataError: null
    });
  }

  applyComposerMetadata = () => {
    const text = (this.state.composerMetadataDraft || "").trim();
    if ( text === "" ) {
      // Empty metadata is treated as "no attachment".
      this.setState({
        composerMetadata: null,
        composerMetadataModalVisible: false,
        composerMetadataError: null
      });
      return;
    }
    let parsed;
    try {
      parsed = JSON.parse(text);
    } catch (e) {
      this.setState({ composerMetadataError: "Invalid JSON: " + e.message });
      return;
    }
    if ( typeof parsed !== "object" || parsed === null || Array.isArray(parsed) ) {
      this.setState({ composerMetadataError: "Metadata must be a JSON object." });
      return;
    }
    this.setState({
      composerMetadata: parsed,
      composerMetadataModalVisible: false,
      composerMetadataError: null
    });
  }

  clearComposerMetadata = () => {
    this.setState({ composerMetadata: null });
  }

  /**
   * Item expansion (long content + metadata)
   */



  retryFailedItem(item) {
    if ( !item || !item.__failed ) return;
    // Posting a thread item is a session-data mutation. retryFailedItem() calls postContent()
    // directly rather than routing through sendItem(), so the gate must be re-checked here — mirrors
    // the choke-point comment on sendItem() itself.
    if ( !canMutateSession(this.props.data.shellInterface) ) return;
    // Drop the failed row, then re-post: a retry is a new post, and leaving the old one would show
    // the same message twice once it lands. Content and metadata go through verbatim — reducing to
    // `content[0].text` would drop everything past the first block on a multi-segment or attachment
    // post.
    if ( this.view.current ) this.view.current.removeLocal(item.__tempId);
    this.pendingTempIds.delete(item.__tempId);
    this.postContent(item.content, item.metadata || null);
  }
  /**
   * Composer handlers
   */

  // Enter/Shift+Enter (unchanged), plus the atomic-token guard: Backspace/Delete touching a
  // `[name]` token for a currently-attached file removes the whole token (+ one adjacent space)
  // instead of eating it a character at a time; a selection overlapping any token expands to clear
  // every token it touches. Typing a character strictly inside a token needs no handler here — it
  // breaks the `[...]` bracket match, so tokenSpans() stops recognizing it on the next render.
  handleKeyDown = (e) => {
    if ( e.key === "Enter" && !e.shiftKey ) {
      e.preventDefault();
      this.sendItem();
      return;
    }
    if ( e.key !== "Backspace" && e.key !== "Delete" ) return;
    const ta = e.target;
    if ( !ta || typeof ta.selectionStart !== "number" ) return;
    const text = this.state.inputValue;
    const names = (this.state.pendingFiles || []).map((chip) => chip.name);
    const spans = tokenSpans(text, names);
    if ( 0 === spans.length ) return;
    const selStart = ta.selectionStart;
    const selEnd = ta.selectionEnd;

    if ( selStart === selEnd ) {
      let span = null;
      if ( "Backspace" === e.key ) {
        span = spans.find((s) => selStart > s.start && selStart <= s.end);
      } else {
        span = spans.find((s) => selStart >= s.start && selStart < s.end);
      }
      if ( !span ) return;
      e.preventDefault();
      let from = span.start;
      let to = span.end;
      if ( "Backspace" === e.key && from > 0 && " " === text[from - 1] ) from -= 1;
      else if ( "Delete" === e.key && to < text.length && " " === text[to] ) to += 1;
      this._setInputAndCaret(text.slice(0, from) + text.slice(to), from);
      return;
    }

    const clipped = spans.filter((s) => s.start < selEnd && s.end > selStart);
    if ( 0 === clipped.length ) return;
    e.preventDefault();
    const from = Math.min(selStart, ...clipped.map((s) => s.start));
    const to = Math.max(selEnd, ...clipped.map((s) => s.end));
    this._setInputAndCaret(text.slice(0, from) + text.slice(to), from);
  };

  // The text is authoritative: a staged file exists only while its `[name]` token is present. Any chip
  // whose token has been deleted (or dissolved by an edit) is dropped — "no reference, no item". Returns
  // the original array when nothing changed, so a keystroke touching no token doesn't churn state.
  attachmentsForText(text, chips) {
    if ( !chips || 0 === chips.length ) return chips;
    const present = new Set(tokenSpans(text, chips.map((chip) => chip.name)).map((span) => span.name));
    const kept = chips.filter((chip) => present.has(chip.name));
    return kept.length === chips.length ? chips : kept;
  }

  // Shared by the atomic-token guard above: sets inputValue (dropping any chip whose token this edit
  // removed), then restores focus and caret once the state update has landed.
  _setInputAndCaret(text, caret) {
    this.setState((prev) => ({
      inputValue: text,
      pendingFiles: this.attachmentsForText(text, prev.pendingFiles)
    }), () => {
      const el = this.textareaRef.current;
      if ( el ) {
        el.focus();
        if ( el.setSelectionRange ) el.setSelectionRange(caret, caret);
      }
    });
  }

  handleTextareaInput = (e) => {
    const value = e.target.value;
    // Reconcile on every edit: a native delete/cut/type-over that removes a token drops its file too.
    this.setState((prev) => ({
      inputValue: value,
      pendingFiles: this.attachmentsForText(value, prev.pendingFiles)
    }));
    const el = e.target;
    el.style.height = "auto";
    const lineHeight = 20;
    const maxHeight = lineHeight * 6;
    el.style.height = Math.min(el.scrollHeight, maxHeight) + "px";
  };

  /**
   * Attachment staging
   */

  // Thin setState wrapper over the pure appendStaged() (attachments.js) — kept pure there so the
  // staging/validation/cap/pasted-name logic is unit-testable without mounting this widget. Also
  // inserts a `[name]` token per newly-staged chip at the caret (read synchronously, before
  // setState, since the textarea's own value hasn't moved yet), threading the caret through each
  // insertion so multiple files staged at once land as consecutive tokens.
  stageFiles = (fileList) => {
    const ta = this.textareaRef.current;
    const caretAtCall = (ta && typeof ta.selectionStart === "number") ? ta.selectionStart : null;
    this.setState((prevState) => {
      const prevChips = prevState.pendingFiles;
      const nextChips = appendStaged(prevChips, fileList);
      const newChips = nextChips.slice(prevChips.length);
      let text = prevState.inputValue;
      let caret = caretAtCall === null ? text.length : caretAtCall;
      newChips.forEach((chip) => {
        const result = insertToken(text, caret, chip.name);
        text = result.text;
        caret = result.caret;
      });
      this._pendingCaret = caret;
      return { pendingFiles: nextChips, inputValue: text };
    }, () => {
      const el = this.textareaRef.current;
      if ( el && typeof this._pendingCaret === "number" ) {
        el.focus();
        if ( el.setSelectionRange ) el.setSelectionRange(this._pendingCaret, this._pendingCaret);
        this._pendingCaret = null;
      }
    });
  };

  // Strips the removed chip's `[name]` token — its occurrence is its index among currently-staged
  // chips sharing that name, so removing the second same-named chip strips the second token. A chip
  // with no `name` (not yet reached in practice, but defensive) leaves the text untouched.
  removeStagedFile = (id) => {
    this.setState((prevState) => {
      const chips = prevState.pendingFiles;
      const target = chips.find((c) => c.id === id);
      let inputValue = prevState.inputValue;
      if ( target && target.name ) {
        const sameName = chips.filter((c) => c.name === target.name);
        const occurrence = sameName.findIndex((c) => c.id === id);
        inputValue = stripToken(prevState.inputValue, target.name, occurrence < 0 ? 0 : occurrence);
      }
      return {
        pendingFiles: chips.filter((c) => c.id !== id),
        inputValue
      };
    });
  };

  // graphs/dnn/shell/index.js's hidden-input template: read the files off the event, then reset
  // the input's value so choosing the same file again still fires a change event. `event.target.files`
  // is a LIVE FileList, so it must be snapshotted (Array.from) BEFORE the reset — clearing the input's
  // value empties that same list, which used to leave "Attach Files" staging nothing while paste (which
  // snapshots via filesFromClipboard) worked.
  handleFileInputChange = (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = null;
    if ( 0 === files.length ) return;
    this.stageFiles(files);
  };

  // filesFromDataTransfer() returns null when the drag carries no "Files" type — a widget-move drag
  // (the workspace hosts' text/plain payloads). Only a real file drag is claimed with
  // preventDefault/stopPropagation; anything else passes through untouched.
  handleComposerDragOver = (e) => {
    if ( !filesFromDataTransfer(e.dataTransfer) ) return;
    e.preventDefault();
    e.stopPropagation();
  };

  handleComposerDrop = (e) => {
    const files = filesFromDataTransfer(e.dataTransfer);
    if ( !files ) return;
    e.preventDefault();
    e.stopPropagation();
    this.stageFiles(files);
  };

  // A plain-text paste is left native (no preventDefault) so the textarea still receives it.
  handleComposerPaste = (e) => {
    const files = filesFromClipboard(e.clipboardData);
    if ( 0 === files.length ) return;
    e.preventDefault();
    this.stageFiles(files);
  };

  /**
   * Author name resolution (cached)
   */

  resolveAuthorName(userId) {
    if ( !userId ) return "unknown";
    const api = this.getApi();
    const cachedResponse = api.usersCacheById ? api.usersCacheById[userId] : null;
    if ( cachedResponse ) {
      const user = (cachedResponse.data && cachedResponse.data["user"]) || {};
      return user["name"] || user["alias"] || user["email"] || userId.slice(-5);
    }
    if ( !this.authorPromises[userId] ) {
      this.authorPromises[userId] = api.usersIdGet(userId).then(() => {
        if ( this.mounted ) this.forceUpdate();
      }, () => {
        delete this.authorPromises[userId];
      });
    }
    return userId.slice(-5);
  }

  /**
   * Rendering
   */

  getClassName() {
    return "playground-thread-node";
  }

  renderHeaderAlias() {
    const alias = this.getBoundAlias();
    if ( !alias ) return null;
    return <Alias value={alias} variant="chip" />;
  }

  renderHeaderActions() {
    const envelope = this.getEnvelopeState();
    const value = envelope.status === "ready" ? envelope.value : null;
    return (
      <EnvelopeControls
        alias={this.getBoundAlias()}
        value={value}
        shellInterface={this.props.data.shellInterface}
        sessionManager={this.context}
        refreshTooltip="Refresh thread"
        // Reloading the envelope value alone doesn't refresh the messages —
        // those come from threadItemsList. Ask the view to resync so a
        // refresh re-fetches the thread items too.
        onRefresh={() => { if ( this.view.current ) this.view.current.resync(); }}
      />
    );
  }



  resolveProfilePath(userId) {
    if ( !userId ) return null;
    // Prefer the user's alias for a cleaner URL, falling back to the id.
    const api = this.getApi();
    const cached = api && api.usersCacheById ? api.usersCacheById[userId] : null;
    const user = cached && cached.data ? cached.data.user || cached.data : null;
    const slug = (user && user.alias) || userId;
    return Navigation.userProfilePath(slug);
  }

  // The hover-preview overlay for a staged attachment chip. An image shows its own thumbnail (from the
  // local File, no upload); anything else shows a type icon. Name, type and size below either way, and
  // a rejected/failed chip's reason so the preview explains why it can't send.
  renderAttachmentPreview(chip, isImage) {
    const url = isImage ? this.previewUrlFor(chip) : null;
    return (
      <Popover id={"attach-preview-" + chip.id} className="playground-thread-attach-popover">
        <Popover.Content>
          {url
            ? <img src={url} alt={chip.name} className="playground-thread-attach-preview-img" />
            : (
              <div className="playground-thread-attach-preview-icon">
                {isImage ? <LuImage /> : <LuPaperclip />}
              </div>
            )
          }
          <div className="playground-thread-attach-preview-name">{chip.name}</div>
          <div className="playground-thread-attach-preview-sub">
            {chip.contentType || "unknown type"}{chip.size ? " · " + Utils.formatBytes(chip.size, 1) : ""}
          </div>
          {chip.error &&
            <div className="playground-thread-attach-preview-err">{chip.error}</div>
          }
        </Popover.Content>
      </Popover>
    );
  }

  renderContent() {
    const { width, height, maxHeight } = this.getEffectiveSize();

    const contentView = Converter.getComponentPortValueData(this.props.data.component, "content_view");
    const envelopeState = this.getEnvelopeState();
    const isReady = envelopeState.status === "ready";

    // One rule for every mutation surface in the playground: it renders only while mutations are
    // actually possible. canMutateSession() collapses ALL the reasons they might not be — no write
    // access, a committed revision, a closed session — so the composer behaves the same way whichever
    // one applies, instead of vanishing for one and lingering disabled for another. Why mutations are
    // blocked isn't surfaced anywhere — the control is simply absent.
    const showComposer = canMutateSession(this.props.data.shellInterface) && isReady;

    return (
      <div ref={this.nodeRef} className="playground-thread-node" style={{ width, height, maxHeight }}>
        {envelopeState.status === "unbound" &&
          this.renderNotice({ variant: "info", message: 'Connect a value to the "thread" port.' })
        }
        {envelopeState.status === "no-session" &&
          this.renderNotice({ variant: "info", message: "Select a session to view this widget." })
        }
        {envelopeState.status === "loading" &&
          this.renderLoading(envelopeState.reason || "Loading…")
        }
        {envelopeState.status === "missing" &&
          this.renderNotice({
            variant: "info",
            message: `"${envelopeState.alias}" is not available in the selected session yet.`,
          })
        }
        {envelopeState.status === "wrong-type" &&
          this.renderNotice({
            variant: "warning",
            message: `The "thread" port expects a "thread" value, but "${envelopeState.alias}" is "${envelopeState.type}".`,
          })
        }
        {/* THE VIEW (common/threadView.js): loading, paging, the live tail, scroll behaviour and
            the item rendering. Mission Control's cell screen mounts the same one for a run's
            transcript, so a change to how a thread READS reaches both without being made twice.
            It is read-only, and posting sits on top of it — the composer below, and the optimistic
            row handed to it through the handles it exposes for exactly that. */}
        {isReady &&
          <ThreadView
            ref={this.view}
            source={this.threadSource()}
            contentView={contentView}
            currentUserId={this.state.currentUserId}
            timezone={this.props.preferences && this.props.preferences["app.timezone"]}
            emptyMessage="No messages yet. Type something below to start the thread."
            onRetry={(item) => this.retryFailedItem(item)}
            fileContext={this.fileContext()}
          />
        }

        {showComposer &&
          <div className="playground-thread-composer">
            <input
              type="file"
              multiple
              ref={this.fileInputRef}
              style={{ display: "none" }}
              onChange={this.handleFileInputChange}
            />
            {this.state.postError &&
              <Alert variant="warning" className="playground-thread-composer-error">
                {this.state.postError}
              </Alert>
            }
            {(this.state.pendingFiles || []).map((chip) => {
              const isImage = chip.contentType && chip.contentType.startsWith("image/");
              const ChipIcon = isImage ? LuImage : LuPaperclip;
              // "rejected" (client-side validation) and "failed" (an upload failure from a prior
              // send attempt, retriable) both read as failed here — the chip's title tooltip below
              // already carries the specific reason.
              const isFailed = "rejected" === chip.status || "failed" === chip.status;
              return (
                // Hover (or focus) the chip to preview what it is before sending — a thumbnail for an
                // image, name/type/size otherwise. Opens on top: the composer sits at the page bottom.
                <OverlayTrigger
                  key={chip.id}
                  placement="top"
                  trigger={["hover", "focus"]}
                  overlay={this.renderAttachmentPreview(chip, isImage)}
                >
                  <button
                    type="button"
                    className={
                      "playground-thread-composer-attached" + (isFailed ? " is-failed" : "")
                    }
                    title={chip.error || undefined}
                  >
                    <ChipIcon className="playground-thread-composer-attached-icon" />
                    <span className="playground-thread-composer-attached-label">
                      {chip.name}
                    </span>
                    <Tooltip text="Remove">
                      <span
                        role="button"
                        tabIndex={0}
                        className="playground-thread-composer-attached-remove"
                        onClick={(e) => { e.stopPropagation(); this.removeStagedFile(chip.id); }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            e.stopPropagation();
                            this.removeStagedFile(chip.id);
                          }
                        }}
                      >
                        <LuX />
                      </span>
                    </Tooltip>
                  </button>
                </OverlayTrigger>
              );
            })}
            {this.state.composerMetadata &&
              <button
                type="button"
                className="playground-thread-composer-attached"
                onClick={this.openComposerMetadataModal}
              >
                <CgDetailsMore className="playground-thread-composer-attached-icon" />
                <span className="playground-thread-composer-attached-label">
                  metadata
                </span>
                <Tooltip text="Remove">
                  <span
                    role="button"
                    tabIndex={0}
                    className="playground-thread-composer-attached-remove"
                    onClick={(e) => { e.stopPropagation(); this.clearComposerMetadata(); }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        e.stopPropagation();
                        this.clearComposerMetadata();
                      }
                    }}
                  >
                    <LuX />
                  </span>
                </Tooltip>
              </button>
            }
            <Form onSubmit={(event) => { event.preventDefault(); this.sendItem(); }}>
              <div
                className="bh-composer-shell"
                onDragOver={this.handleComposerDragOver}
                onDrop={this.handleComposerDrop}
              >
                <Dropdown drop="up">
                  <Dropdown.Toggle as={ComposerAttachToggle} />
                  <Dropdown.Menu>
                    <Dropdown.Item onClick={() => this.fileInputRef.current.click()}>
                      <LuPaperclip />&nbsp;&nbsp;Attach Files
                    </Dropdown.Item>
                    <Dropdown.Item onClick={this.openComposerMetadataModal}>
                      <CgDetailsMore />&nbsp;&nbsp;{this.state.composerMetadata ? "Edit Metadata" : "Add Metadata"}
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
                <Form.Control
                  as="textarea"
                  rows={1}
                  ref={this.textareaRef}
                  className="bh-composer-textarea nodrag nowheel"
                  placeholder="Type a message..."
                  value={this.state.inputValue}
                  onChange={this.handleTextareaInput}
                  onKeyDown={this.handleKeyDown}
                  onPaste={this.handleComposerPaste}
                />
                <Tooltip text={this.sendTooltipText()}>
                  <Button
                    className="bh-composer-send"
                    disabled={!this.canSend()}
                    onClick={() => this.sendItem()}
                  >
                    <VscSend />
                  </Button>
                </Tooltip>
              </div>
            </Form>
          </div>
        }

        {this.renderMetadataModal()}
      </div>
    );
  }

  renderMetadataModal() {
    if ( !this.state.composerMetadataModalVisible ) return null;
    return (
      <Modal
        show={this.state.composerMetadataModalVisible}
        onHide={this.closeComposerMetadataModal}
        centered
        size="lg"
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>Attach Metadata</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Editor
            name="playground-thread-composer-metadata-editor"
            width="100%"
            mode="json"
            wrapEnabled="true"
            value={this.state.composerMetadataDraft}
            onChange={(value) => this.setState({
              composerMetadataDraft: value, composerMetadataError: null
            })}
            minLines={5}
            maxLines={24}
            setOptions={{ printMarginColumn: 120 }}
          />
          <div style={{ marginTop: "10px", color: "var(--bh-text-muted-2)", fontSize: "13px" }}>
            Metadata attached to the next sent message. Must be a JSON object.
            Leave empty to send without metadata.
          </div>
          {this.state.composerMetadataError &&
            <Alert variant="warning" style={{ marginTop: "10px", marginBottom: 0 }}>
              {this.state.composerMetadataError}
            </Alert>
          }
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={this.closeComposerMetadataModal}>
            Cancel
          </Button>
          <Button variant="success" onClick={this.applyComposerMetadata}>
            Apply
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }
}

export default withPreferences(
  ThreadNode,
  ["app.timezone"],
  // Resolve preferences with the same org/blob scope the playground shell
  // uses, so blob- or org-level timezone overrides are honored. Without
  // this, withPreferences falls back to the user-level scope only and any
  // blob-scoped timezone is silently ignored.
  props => props.data.shellInterface.getScope()
);
